import { useCallback } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { useFtpStore } from '../../store/ftpStore';
import { runFolderMerge, labelUnder } from '../../utils/folderMerge';
import { settleIfStillQueued } from './transferPin';
import { FolderTooLargeError, planFolder, browserDirectoryLister } from '../../utils/folderTransfer';
import {
  baseName, errText, joinLocal, parentLocal, joinRemote, newTransferId, parentRemote,
} from './ftpViewHelpers';

/** FtpView's folder transfers: mixed-selection downloads, the Upload dialog, folder planning, merge-on-upload and folder download. */
export function useFtpFolderTransfers({
  t, remoteNamesAt, localNamesAt, showNotice, startPathUpload, startDownload, uploadBrowserFile,
  askConflict, transferBatch,
}) {
  /** Download a mixed selection: folders as folders, files as ONE protected batch. */
  const downloadTargets = (targets) => {
    targets.filter((x) => x.isDir).forEach((x) => downloadFolder(x));
    transferBatch(
      'download',
      targets.filter((x) => !x.isDir).map((x) => ({ name: x.name, start: (meta) => startDownload(x, undefined, meta) }))
    );
  };

  const pickUploadDialog = useCallback(async () => {
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const picked = await open({ multiple: true });
      if (!picked) return;
      const paths = Array.isArray(picked) ? picked : [picked];
      await transferBatch(
        'upload',
        paths.map((p) => ({ name: baseName(String(p)), start: (meta) => startPathUpload(String(p), undefined, undefined, meta) }))
      );
    } catch (e) {
      console.warn('[ftp] upload dialog failed:', errText(e));
    }
  }, [startPathUpload, transferBatch]);

  /* ── Drops ───────────────────────────────────────────────────────────── */

  /**
   * Drop a dragged selection onto a folder ROW in the same pane = MOVE.
   *
   * `ftp_rename` is exactly the primitive: on both SFTP and FTP, renaming to a
   * path in another directory IS the move, so this needs no new Rust.
   *
   * **Remote only.** The local pane is the user's own disk and has no move
   * command behind it; lighting up a local folder and then doing nothing would
   * be worse than not lighting up.
   *
   * Four guards, and each of them is a way to destroy data or hang:
   *  - a folder cannot be moved into ITSELF;
   *  - nor into its own DESCENDANT, which on a server that allows it detaches
   *    the subtree and on one that does not fails halfway through a batch;
   *  - an entry already IN the destination is skipped, not renamed onto itself;
   *  - and a name that already exists at the destination is **refused**, because
   *    `ftp_rename` on a live server may happily replace it and the user dropped
   *    a file, they did not ask to overwrite one.
   */
  /* ── Folder transfers ─────────────────────────────────────────────────── */

  /**
   * Turn a planning failure into one sentence for the user. A too-large tree is
   * an EXPECTED outcome with a clear remedy, not an error to dump raw.
   */
  const reportFolderPlanError = useCallback(
    (err) => {
      if (err instanceof FolderTooLargeError) {
        showNotice(
          t('ftp.folderTooLarge', {
            what:
              err.kind === 'depth'
                ? t('ftp.folderTooDeep', { limit: err.limit })
                : t('ftp.folderTooMany', { limit: err.limit }),
          })
        );
      } else {
        showNotice(errText(err));
      }
    },
    [t, showNotice]
  );

  const reportSkipped = useCallback(
    (skipped) => {
      if (!skipped.length) return;
      showNotice(
        t('ftp.skippedNames', { count: skipped.length, names: skipped.slice(0, 5).join(', ') })
      );
    },
    [t, showNotice]
  );

  /**
   * Download a remote FOLDER into the local pane's current directory.
   *
   * The whole tree is planned first (`planFolder`), so a tree that is too big or
   * too deep is refused before anything is created. Then every directory is
   * made — through `ftp_local_mkdir`, which is containment-checked against the
   * folder the user chose, because these names came from the server — and only
   * then are the files queued, each into its own subdirectory.
   */
  const downloadFolder = useCallback(
    async (entry) => {
      const s = useFtpStore.getState();
      const connId = s.activeConnId;
      if (!connId || s.connState[connId] !== 'connected' || !s.localPath) return;
      const root = s.localPath;
      let taken;
      try {
        taken = await localNamesAt(root);
      } catch (err) {
        // Cannot see the destination, so cannot know what would be overwritten.
        showNotice(errText(err));
        return;
      }
      if (taken.has(entry.name)) {
        showNotice(t('ftp.nameExists', { name: entry.name }));
        return;
      }
      let plan;
      try {
        plan = await planFolder(
          // Hidden files included: a folder download is expected to be the
          // folder, and silently leaving out `.env` or `.htaccess` would be a
          // copy that looks complete and is not.
          (path) => invoke('ftp_list_dir', { connId, path, showHidden: true }),
          entry.path,
          joinLocal(root, entry.name),
          joinLocal
        );
      } catch (err) {
        reportFolderPlanError(err);
        return;
      }
      try {
        for (const dir of plan.dirs) {
          // eslint-disable-next-line no-await-in-loop
          await invoke('ftp_local_mkdir', { path: dir, root });
        }
      } catch (err) {
        // A directory could not be made — most often the containment check.
        // Nothing has been downloaded yet, so stopping here leaves no half-copy.
        showNotice(errText(err));
        return;
      }
      for (const f of plan.files) {
        startDownload({ name: f.name, path: f.from, size: f.size }, parentLocal(f.to));
      }
      reportSkipped(plan.skipped);
      useFtpStore.getState().listLocal(root).catch(() => {});
    },
    // localNamesAt is recreated every render and was never a dependency (kept as-is by the split).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, showNotice, startDownload, reportFolderPlanError, reportSkipped]
  );

  /**
   * Upload a folder tree into `destDir` (the pane's directory, or a folder row
   * it was dropped onto) — and MERGE it when a folder of that name is already
   * there, like FileZilla / WinSCP / Finder's "Merge": missing sub-folders are
   * created, new files uploaded, and every file that already exists is put to
   * the user (Replace / Skip / … all / Cancel) before anything moves. The
   * deciding is utils/folderMerge.js, executed in tests over every branch.
   *
   * Only a FILE with the folder's name is still refused: a folder cannot be
   * merged into a file, whatever the user answers.
   *
   * @param {string} name                 the dropped folder's name
   * @param {string|undefined} destDir
   * @param {(destRoot: string) => Promise<object>} makePlan   `planFolder` over the source
   * @param {(file: object, meta: object) => Promise<void>} uploadOne
   */
  const uploadTree = useCallback(
    async (name, destDir, makePlan, uploadOne) => {
      const s = useFtpStore.getState();
      const connId = s.activeConnId;
      if (!connId || s.connState[connId] !== 'connected') return;
      const remoteRoot = destDir || s.getRemotePath();
      let taken;
      try {
        taken = await remoteNamesAt(remoteRoot);
      } catch (err) {
        showNotice(errText(err));
        return;
      }
      if (taken.get(name) === false) {
        showNotice(t('ftp.folderOverFile', { name }));
        return;
      }
      const destRoot = joinRemote(remoteRoot, name);
      let plan;
      try {
        plan = await makePlan(destRoot);
      } catch (err) {
        reportFolderPlanError(err);
        return;
      }
      const group = { groupId: newTransferId(), groupName: name, groupPath: destRoot };
      let result;
      try {
        result = await runFolderMerge({
          plan,
          rootExists: taken.get(name) === true,
          // Hidden files included: an `.htaccess` the check cannot see is one
          // the merge would overwrite unasked.
          listDir: (path) => invoke('ftp_list_dir', { connId, path, showHidden: true }),
          // Straight to the command, not `mkdirRemote`: that one re-lists the
          // pane after every call, which on a deep tree is hundreds of listings.
          mkdir: (path) => invoke('ftp_mkdir', { connId, path }),
          ask: (question) => askConflict({ ...question, kind: 'upload' }),
          label: labelUnder(remoteRoot),
          onQueue: (files, replaced) => {
            // Every file gets its row up front, "queued", so the folder's group
            // can say "3 of 12 files" from the first second.
            for (const f of files) {
              f.transferId = newTransferId();
              useFtpStore.getState().upsertTransfer({
                ...group,
                transferId: f.transferId,
                connId,
                kind: 'upload',
                name: f.name,
                path: f.to,
                total: f.size || 0,
                transferred: 0,
                queued: true,
                replaced: replaced.has(f) || undefined,
                done: false,
                error: null,
                canceled: false,
              });
            }
          },
          onSkip: (skippedFiles) => {
            for (const f of skippedFiles) {
              useFtpStore.getState().upsertTransfer({
                ...group,
                transferId: newTransferId(),
                connId,
                kind: 'upload',
                name: f.name,
                path: f.to,
                total: f.size || 0,
                transferred: 0,
                done: true,
                skipped: true,
                error: null,
                canceled: false,
              });
            }
          },
          upload: async (f, replaced) => {
            try {
              await uploadOne(f, { ...group, connId, transferId: f.transferId, replaced });
            } catch (err) {
              settleIfStillQueued(f.transferId, { error: errText(err) });
              throw err;
            }
            // Returned without starting (connection gone): not "Queued" forever.
            settleIfStillQueued(f.transferId, { canceled: true });
          },
        });
      } catch (err) {
        // Listing or a mkdir failed. Nothing has been uploaded at that point,
        // so stopping here leaves no half-copied tree.
        showNotice(errText(err));
        return;
      }
      if (result.cancelled) showNotice(t('ftp.mergeCancelled', { name }));
      if (result.blocked.length) {
        showNotice(
          t('ftp.mergeBlocked', { names: result.blocked.map((b) => b.label).slice(0, 5).join(', ') })
        );
      }
      if (result.failed.length) {
        showNotice(result.failed.map((f) => `${f.name}: ${f.error}`).join('; '));
      }
      reportSkipped(plan.skipped);
      useFtpStore.getState().listRemote(useFtpStore.getState().getRemotePath()).catch(() => {});
    },
    // remoteNamesAt is recreated every render and was never a dependency (kept as-is by the split).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, showNotice, askConflict, reportFolderPlanError, reportSkipped]
  );

  /** Upload a LOCAL folder (dragged from the local pane) into `destDir`. */
  const uploadFolder = useCallback(
    (localDirPath, name, destDir) =>
      uploadTree(
        name,
        destDir,
        (destRoot) =>
          planFolder(
            async (path) => (await invoke('ftp_local_list', { path }))?.entries || [],
            localDirPath,
            destRoot,
            joinRemote
          ),
        // Rust streams it and reports progress itself.
        async (f, meta) => startPathUpload(f.from, f.name, parentRemote(f.to), meta)
      ),
    [uploadTree, startPathUpload]
  );

  /** Upload a folder dropped from the OS. The browser hands over directory
   *  ENTRIES, not paths, so the walk goes through the entry API — including the
   *  loop that reads past its first hundred children. */
  const uploadDroppedFolder = useCallback(
    (dirEntry, destDir) =>
      uploadTree(
        dirEntry.name,
        destDir,
        (destRoot) => planFolder(browserDirectoryLister().list, dirEntry, destRoot, joinRemote),
        async (f, meta) => {
          const file = await new Promise((resolve) => f.from.file(resolve, () => resolve(null)));
          if (!file) throw new Error(t('ftp.unreadableFile'));
          // Awaited: each browser upload streams its bytes through JS in
          // chunks, and a few hundred in parallel would hold every file in
          // memory at once.
          await uploadBrowserFile(file, parentRemote(f.to), meta);
        }
      ),
    [uploadTree, uploadBrowserFile, t]
  );

  return { downloadTargets, pickUploadDialog, downloadFolder, uploadFolder, uploadDroppedFolder };
}
